import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  LoaderCircle,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useUserOrgId } from 'shared/session';
import { completeCredentialRequest, fetchCredentialRequest } from './credentialSetupApi';

const emptyObject = () => ({});

const CredentialSetupModal = ({ requestAction, onClose, onCompleted }) => {
  const { data: orgId } = useUserOrgId();
  const [request, setRequest] = useState(null);
  const [values, setValues] = useState(emptyObject);
  const [visibleFields, setVisibleFields] = useState(emptyObject);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setRequest(null);
    setValues({});
    setVisibleFields({});
    setError('');
    if (!requestAction?.requestId || !orgId) return () => undefined;

    setIsLoading(true);
    fetchCredentialRequest({ requestId: requestAction.requestId, orgId })
      .then((payload) => {
        if (!cancelled) setRequest(payload);
      })
      .catch((nextError) => {
        if (!cancelled) setError(nextError?.message || 'Unable to load credential request');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      setValues({});
      setVisibleFields({});
    };
  }, [requestAction?.requestId, orgId]);

  const fields = useMemo(() => (Array.isArray(request?.fields) ? request.fields : []), [request]);
  const canSubmit =
    request?.status === 'pending' &&
    fields.length > 0 &&
    fields.every((field) => String(values[field.key] || '').length > 0) &&
    !isSubmitting;

  const close = () => {
    if (isSubmitting) return;
    setValues({});
    setVisibleFields({});
    onClose?.();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit || !orgId) return;
    setIsSubmitting(true);
    setError('');
    try {
      const result = await completeCredentialRequest({
        requestId: request.id,
        orgId,
        values,
      });
      setValues({});
      setVisibleFields({});
      onCompleted?.(result);
    } catch (nextError) {
      setError(nextError?.message || 'Unable to store credentials');
    } finally {
      setIsSubmitting(false);
    }
  };

  const destinations = Array.isArray(request?.display_destinations)
    ? request.display_destinations
    : [];

  return (
    <Transition appear show={Boolean(requestAction)} as={Fragment}>
      <Dialog as="div" className="relative z-[70]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-950/45" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg overflow-hidden rounded-lg bg-white shadow-2xl ring-1 ring-black/10">
                <div className="flex items-start justify-between border-b border-neutral-200 px-5 py-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
                      <ShieldCheck size={22} aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <Dialog.Title className="truncate text-base font-semibold text-neutral-950">
                        Secure credential setup
                      </Dialog.Title>
                      <p className="truncate text-sm text-neutral-600">
                        {request?.connection_name || requestAction?.connectionName || 'Integration connection'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={close}
                    disabled={isSubmitting}
                    className="ml-3 rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 disabled:opacity-50"
                    aria-label="Close credential setup"
                    title="Close"
                  >
                    <X size={18} />
                  </button>
                </div>

                <form onSubmit={submit}>
                  <div className="space-y-5 px-5 py-5">
                    <div className="flex gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-950">
                      <KeyRound className="mt-0.5 shrink-0" size={17} aria-hidden="true" />
                      <p>
                        Sent directly to the credential broker. Values are not added to chat or tool history.
                      </p>
                    </div>

                    {destinations.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-semibold uppercase text-neutral-500">
                          Approved destinations
                        </p>
                        <div className="space-y-1.5">
                          {destinations.map((destination) => (
                            <div
                              key={destination}
                              className="flex items-center gap-2 rounded-md bg-neutral-100 px-3 py-2 font-mono text-xs text-neutral-800"
                            >
                              <CheckCircle2 className="shrink-0 text-emerald-600" size={15} />
                              <span className="min-w-0 break-all">{destination}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {isLoading && (
                      <div className="flex min-h-28 items-center justify-center text-neutral-600">
                        <LoaderCircle className="mr-2 animate-spin" size={18} />
                        Loading secure form
                      </div>
                    )}

                    {!isLoading && request?.status !== 'pending' && request && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-950">
                        This credential request is {request.status}. Ask your agent to create a new request.
                      </div>
                    )}

                    {!isLoading && request?.status === 'pending' && (
                      <div className="space-y-4">
                        {fields.map((field) => {
                          const isVisible = Boolean(visibleFields[field.key]);
                          return (
                            <div key={field.key}>
                              <label
                                htmlFor={`integration-credential-${field.key}`}
                                className="mb-1.5 block text-sm font-medium text-neutral-800"
                              >
                                {field.label}
                              </label>
                              <div className="relative">
                                <input
                                  id={`integration-credential-${field.key}`}
                                  type={isVisible ? 'text' : 'password'}
                                  autoComplete={field.autocomplete || 'off'}
                                  value={values[field.key] || ''}
                                  onChange={(event) =>
                                    setValues((current) => ({
                                      ...current,
                                      [field.key]: event.target.value,
                                    }))
                                  }
                                  required
                                  spellCheck={false}
                                  className="h-10 w-full rounded-md border border-neutral-300 bg-white px-3 pr-10 text-sm text-neutral-950 outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setVisibleFields((current) => ({
                                      ...current,
                                      [field.key]: !current[field.key],
                                    }))
                                  }
                                  className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-neutral-500 hover:text-neutral-900"
                                  aria-label={isVisible ? `Hide ${field.label}` : `Show ${field.label}`}
                                  title={isVisible ? 'Hide value' : 'Show value'}
                                >
                                  {isVisible ? <EyeOff size={17} /> : <Eye size={17} />}
                                </button>
                              </div>
                              {field.help_text && (
                                <p className="mt-1.5 text-xs text-neutral-500">{field.help_text}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {error && (
                      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                        {error}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-end gap-2 border-t border-neutral-200 bg-neutral-50 px-5 py-4">
                    <button
                      type="button"
                      onClick={close}
                      disabled={isSubmitting}
                      className="h-9 rounded-md px-3 text-sm font-medium text-neutral-700 hover:bg-neutral-200 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className="flex h-9 items-center gap-2 rounded-md bg-neutral-950 px-4 text-sm font-semibold text-white hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-45"
                    >
                      {isSubmitting && <LoaderCircle className="animate-spin" size={16} />}
                      Store securely
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CredentialSetupModal;

